/* Endpoint de chat IA — suporta multimodal + modo SOAP + contexto plantão */
import { fetchWithAuth } from './client';
import { resetAnonId } from '../utils/anonId';
import type {
  ChatRequestPayload,
  MessageRole,
  AIMode,
  ContentBlock,
  DocRequestType,
} from '../types';

/**
 * Opções aditivas do envio (09/07/2026 — barra "Gerar documento").
 * Campos opcionais no body: backend antigo ignora, nada quebra.
 */
export interface SendMessageOptions {
  /** Tipo de documento pedido (telemetria backend conta documentos) */
  docRequest?: DocRequestType;
}

/**
 * Envia mensagem para /api/ai/chat e retorna resposta da IA.
 * Backend usa Claude Sonnet 4.6 com prompt caching.
 *
 * Cleanup 09/05/2026: removido `getUserIdFromJwt()` helper + `userId` do body.
 * Backend `b3fbcb62` agora extrai userId do JWT Bearer (middleware tolerante 8.B.3).
 *
 * Trial v2 (02/07/2026, TRIAL_V2_API.md): quando `anonId` é passado (anônimo
 * com flag trial_v2 ligada), envia headers X-Anon-Id + X-Trial-Surface: web.
 * Com flag off o param NUNCA é passado (null) — request idêntico ao atual.
 *
 * @param messages - Histórico de mensagens (pode conter content blocks multimodal)
 * @param sessionId - ID da sessão atual (UUID v4)
 * @param mode - 'chat' (conversa livre) ou 'prontuario' (SOAP estruturado)
 * @param plantaoContext - Contexto do plantão ativo ou null
 * @param anonId - UUID v4 anônimo do trial v2 (null = fluxo atual, sem headers)
 * @param options - Campos aditivos opcionais (doc_request etc.)
 */
export async function sendMessage(
  messages: Array<{ role: MessageRole; content: string | ContentBlock[] }>,
  sessionId: string,
  mode: AIMode = 'chat',
  plantaoContext: string | null = null,
  anonId: string | null = null,
  options: SendMessageOptions = {},
): Promise<string> {
  const payload: ChatRequestPayload = {
    messages,
    sessionId,
    mode,
    plantao_context: plantaoContext,
  };

  // Barra "Gerar documento" — campo aditivo, só presente quando pedido
  if (options.docRequest !== undefined) {
    payload.doc_request = options.docRequest;
  }

  // Telemetria "% casos com exame" (09/07/2026): mensagem com content
  // blocks não-texto (image/document) leva anexo → has_attachments: true.
  // Campo aditivo, só presente quando true; backend antigo ignora.
  const temAnexo = messages.some(
    (m) => Array.isArray(m.content) && m.content.some((b) => b.type !== 'text'),
  );
  if (temAnexo) {
    payload.has_attachments = true;
  }

  const trialHeaders: Record<string, string> =
    anonId !== null
      ? { 'X-Anon-Id': anonId, 'X-Trial-Surface': 'web' }
      : {};

  const response = await fetchWithAuth('/api/ai/chat', {
    method: 'POST',
    headers: trialHeaders,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as {
      error?: string;
      type?: string;
      day?: number;
    };

    // Erros discriminados para o hook de chat tratar corretamente
    if (response.status === 402) {
      throw { type: 'credits_exhausted', message: body.error ?? 'Créditos esgotados' };
    }
    if (response.status === 429) {
      throw { type: 'fair_use_exceeded', message: body.error ?? 'Limite de uso atingido' };
    }
    if (response.status === 401) {
      throw { type: 'unauthorized', message: body.error ?? 'Sessão expirada' };
    }
    // Trial v2 — 403 anônimo: dia 2-3 pede email; dia 4+ paywall
    if (response.status === 403 && body.error === 'email_required') {
      throw { type: 'email_required', message: body.error, day: body.day };
    }
    if (response.status === 403 && body.error === 'trial_ended') {
      throw { type: 'trial_ended', message: body.error, day: body.day };
    }
    // Trial v2 — UUID inválido no X-Anon-Id: regenera (safety net) e pede retry
    if (response.status === 400 && body.error === 'invalid_anon_id') {
      resetAnonId();
      throw { type: 'unknown', message: 'Identidade renovada — tenta enviar de novo.' };
    }

    throw { type: 'unknown', message: body.error ?? 'Erro no chat' };
  }

  const data = (await response.json()) as {
    message?: string;
    content?: string;
    response?: string;
  };

  // Backend retorna { message } (canônico). Aceita { content } / { response } por backcompat.
  const text = data.message ?? data.content ?? data.response ?? '';
  if (!text) throw { type: 'unknown', message: 'Resposta vazia do servidor' };

  return text;
}
